// Reading the assistant's SSE reply, and the error lines the chat shows.
//
// The server ends every turn with a `done` or an `error` event. A stream that
// stops without one was cut off (a dropped connection, a proxy timeout, a
// server restart) — it used to read as a normal, finished reply.

export const ASSISTANT_CUT_OFF_MESSAGE =
  "The reply was cut off before the assistant finished. Anything it already did (like cart changes) is saved — check your cart before asking again.";

export const ASSISTANT_GENERIC_ERROR =
  'Something went wrong while the assistant was working on that. Send your message again, or contact support if it keeps happening.';

const OFFLINE_MESSAGE = "Couldn't reach MealOps. Check your connection and send your message again.";

const parseEventBlock = (rawEvent) => {
  let eventName = 'message';
  let dataText = '';
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataText += line.slice(5).trim();
  }
  if (!dataText) return null;
  let data = null;
  try {
    data = JSON.parse(dataText);
  } catch {
    data = null;
  }
  return { eventName, data };
};

/**
 * Read an SSE body, calling `onEvent(name, data)` per event. Resolves to
 * { terminal } — 'done' or 'error' when the server finished the turn, null
 * when the stream ended (or broke) without saying so.
 */
export async function readAssistantStream(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminal = null;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const parsed = parseEventBlock(buffer.slice(0, separatorIndex));
        buffer = buffer.slice(separatorIndex + 2);
        if (parsed) {
          if (parsed.eventName === 'done' || parsed.eventName === 'error') terminal = parsed.eventName;
          onEvent(parsed.eventName, parsed.data);
        }
        separatorIndex = buffer.indexOf('\n\n');
      }
    }
  } catch {
    // A connection that breaks mid-reply is a cut-off, not a fresh error.
    return { terminal: null };
  }
  return { terminal };
}

// Raw provider/API error text: JSON bodies, "529 {…}" status-prefixed bodies,
// stack-ish or very long messages. None of it means anything to a coach.
const looksRaw = (text) =>
  /[{[]\s*"/.test(text) ||
  /"\w+"\s*:/.test(text) ||
  /^\d{3}\b/.test(text) ||
  /\b(?:TypeError|ReferenceError|SyntaxError|ECONN\w+|ETIMEDOUT|PGRST\d+|status code)\b/.test(text) ||
  text.length > 280;

/** The line the chat's error bubble shows for an error message (and HTTP status, when known). */
export function coachFacingErrorText(raw, { status = null } = {}) {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (/^(Failed to fetch|Load failed|NetworkError\b|fetch failed)/i.test(text)) return OFFLINE_MESSAGE;
  if (status === 401) return 'Your session has expired. Refresh the page and sign in again.';
  if (status === 429) return 'Too many messages in a short time. Wait a minute, then send your message again.';
  if (!text || looksRaw(text)) return ASSISTANT_GENERIC_ERROR;
  return text;
}
